/* A library for working with SockJS websockets.
* This is somewhat based on rxjs-websockets but with a lot of customisations
* for freelancer.
*/
import { Inject, Injectable } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import {
  concat,
  distinctUntilChanged,
  first,
  map,
  pairwise,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { TypedAction } from '../actions';
import { DATASTORE_CONFIG } from '../datastore.config';
import { DatastoreConfig } from '../datastore.interface';
import { setDiff, toNumber } from '../helpers';
import { StoreState } from '../store.model';
import { SockMessageSend } from './message-send-event.model';
import { MultiSet } from './multiset';
import {
  isWebsocketMessage,
  WebsocketEvent,
  WebsocketMessage,
  WebsocketServerEvent,
} from './server-message-event.model';
import {
  generateChannelSubMessage,
  generateHeader,
  generateOnlineOfflineSub,
  generateOnlineOfflineUnsub,
  Socket,
} from './sock-js';

@Injectable()
export class WebSocketService {
  private sock: Socket;
  private connectionStatus = new BehaviorSubject<number>(0);

  public fromServerStream = new Subject<WebsocketServerEvent>();

  private open = false;
  private channelSubscriptions = new Set<number>();
  private onlineOfflineSubscriptions = new MultiSet<number>();
  private unsubDebounceTime?: number; // TODO: T35544
  private connectRetryCount = 0;

  constructor(
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
    private auth: Auth,
    private store: Store<StoreState>,
  ) {
    this.create();
  }

  private create() {
    this.auth.authState.pipe(distinctUntilChanged()).subscribe(auth => {
      this.open = false;

      // FIXME: handle logged-out / logging-out
      if (!auth) {
        return;
      }

      const closed = () => {
        if (!this.open) {
          return;
        }

        this.connectionStatus.next(this.connectionStatus.getValue() - 1);
        this.open = false;
      };

      this.sock = new Socket(this.datastoreConfig.webSocketUrl);

      this.sock.onopen = () => {
        this.open = true;
        this.connectRetryCount = 0;
        this.connectionStatus.next(this.connectionStatus.getValue() + 1);
        this.sendEvent(generateHeader(auth));
        this.subscribeToEvents();
      };

      // Only send events with actual messages, not handshaking messages.
      this.sock.onmessage = event => {
        const decodedEvent = JSON.parse(event.data);
        if (isWebsocketMessage(decodedEvent)) {
          this.processEvent(decodedEvent, auth.userId);
        }
      };

      this.sock.onclose = event => {
        closed();
        if (event.wasClean) {
          this.fromServerStream.complete();
        } else {
          // FIXME: use global error toast service to display something when
          // the websocket is reconnecting (and let the user force reconnect?)
          const retryDelay =
            2 ** this.connectRetryCount * 1000 +
            Math.round(Math.random() * 1000);
          console.warn(`websocket disconnected, retrying in ${retryDelay}ms`);
          Observable
            // race between the retry timeout & the online event..
            .race(
              Observable.timer(retryDelay).pipe(
                tap(() => {
                  // retryDelay is capped to ~60 seconds
                  if (this.connectRetryCount <= 5) {
                    this.connectRetryCount++;
                  }
                  console.warn('reconnecting (timeout)...');
                }),
              ),
              Observable.fromEvent(window, 'online').pipe(
                tap(() => {
                  this.connectRetryCount = 0;
                  console.warn('reconnecting (online event)...');
                }),
              ),
            )
            // ..take the first one..
            .pipe(first())
            // and re-create the websocket.
            .subscribe(() => {
              this.create();
            });
        }
      };

      return () => {
        if (this.sock) {
          closed();
          this.sock.close();
        }
      };
    });
  }

  private sendEvent(message: SockMessageSend) {
    if (this.open) {
      this.sock.send(message);
    } else {
      console.error('Tried sending a message to a closed websocket.');
    }
  }

  private processEvent(event: WebsocketMessage, toUserId: string) {
    const { body }: { body: WebsocketEvent } = event;

    const action: TypedAction = {
      type: 'WS_MESSAGE',
      payload: {
        ...body,
        toUserId, // all WebSocket messages are tied to the current user
      },
    };
    this.store.dispatch(action);
    // Once the store is updated then we can let everyone know.
    this.fromServerStream.next(event);
  }

  /* We buffer the user subscriptions and unsubscriptions by 1 second
   * to reduce backend load.
   * It's possible to have subscribed and unsubscriped within that buffer
   * so we remove such sub/unsub (and reverse) manually.
   */
  private subscribeToEvents() {
    // Subscribe to existing subscriptions
    const existingSubscriptions = this.onlineOfflineSubscriptions.values();
    if (existingSubscriptions.length) {
      this.sendEvent(generateOnlineOfflineSub(existingSubscriptions));
    }

    if (this.channelSubscriptions.size) {
      this.sendEvent(
        generateChannelSubMessage(Array.from(this.channelSubscriptions)),
      );
    }

    this.sendEvent(
      generateOnlineOfflineSub(this.onlineOfflineSubscriptions.values()),
    );
  }

  subscribeOnlineOffline(userIds: Observable<ReadonlyArray<string>>) {
    // Watch the user id stream, calculate watch for changes,
    // subscribe/unsubscribe to any additions/removals respectively
    // and return the user status for the user who is subscribed to.
    // We add `undefined` to the end so we can get the last unsub.
    const incomingUserIds = userIds.pipe(
      distinctUntilChanged(),
      map(ids => ids.map(id => toNumber(id))),
      startWith<ReadonlyArray<number>>([]),
      concat(Observable.of([])),
      pairwise(),
    );

    // Work out which user ids are just added to this subscription.
    const addedUserIds = incomingUserIds.pipe(
      map(([previous, current]) => setDiff(current, previous)),
    );

    // Work out which user ids are just removed to this subscription.
    const removedUserIds = incomingUserIds.pipe(
      map(([previous, current]) => setDiff(previous, current)),
    );

    // Add new ids to subscription counts and return ones newly added to the global collection
    const newlyAddedUserIds = addedUserIds.pipe(
      map(ids =>
        ids.filter(id => {
          this.onlineOfflineSubscriptions.add(id);
          return this.onlineOfflineSubscriptions.multiplicity(id) === 1;
        }),
      ),
    );

    // Add new ids to subscription counts and return ones newly added to the global collection
    const newlyRemovedUserIds = removedUserIds.pipe(
      map(ids =>
        ids.filter(id => {
          if (this.onlineOfflineSubscriptions.multiplicity(id) === 0) {
            console.warn(`Tried removing user ${id} who is already removed.`);
          }
          this.onlineOfflineSubscriptions.remove(id);
          return this.onlineOfflineSubscriptions.multiplicity(id) === 0;
        }),
      ),
    );

    // Send websocket events and return an Observable.never()
    return Observable.never().pipe(
      withLatestFrom(
        newlyAddedUserIds.pipe(
          tap(ids => {
            if (this.open && ids.length) {
              this.sendEvent(generateOnlineOfflineSub(ids));
            }
          }),
        ),
        newlyRemovedUserIds.pipe(
          tap(ids => {
            if (this.open && ids.length) {
              this.sendEvent(generateOnlineOfflineUnsub(ids));
            }
          }),
        ),
      ),
    );
  }

  setChannels(channels: number[]) {
    const currentSubscriptions = Array.from(this.channelSubscriptions);
    const arrayDiff = (a: number[], b: number[]) =>
      a.filter(x => !b.includes(x));

    this.unsubscribeChannels(arrayDiff(currentSubscriptions, channels));
    this.subscribeChannels(arrayDiff(channels, currentSubscriptions));
  }

  private subscribeChannels(channels: number[]) {
    if (channels.length) {
      if (this.open) {
        this.sendEvent(generateChannelSubMessage(channels));
      }
      channels.forEach(channel => this.channelSubscriptions.add(channel));
    }
  }

  private unsubscribeChannels(channels: number[]) {
    if (channels.length) {
      channels.forEach(channel => this.channelSubscriptions.delete(channel));

      /* TODO: Do this properly.
       * Unfortunately the websocket backend doesn't support unsubscribing
       * and so we need to disconnect and reconnect the websocket to unsub :(
       *
       * Ref T35544
       */
      if (this.unsubDebounceTime) {
        clearTimeout(this.unsubDebounceTime);
      }
      this.unsubDebounceTime = window.setTimeout(() => {
        this.unsubDebounceTime = undefined;
        this.sock.close();
        window.setTimeout(() => this.create(), 1000);

        // We should also disconnect the other websocket that libnotify has.
        const w = <any>window;
        if (w.libnotify && w.libnotify.close) {
          w.libnotify.close();
        }
      }, 5000);
    }
  }
}



// WEBPACK FOOTER //
// ./src/@freelancer/datastore/core/websocket/websocket.ts